/**
 * Who and where an event happened, attached to every Sentry event of the request.
 *
 * Tags are set on Sentry's ISOLATION scope, which `@sentry/nextjs` forks per
 * incoming request, so a tag set while resolving one request never reaches
 * another. Each tag is also set as a scope ATTRIBUTE, because a log record
 * carries attributes and not tags; that is what lets a log line be filtered by
 * market. The resolvers that call these are `cache()`d per request, so each
 * runs once.
 *
 * ⚠️ **Identifiers only.** `user.id` is the central `User.id` or the tenant's
 * customer id, prefixed so the two id spaces cannot collide; never an email,
 * phone or name. `sendDefaultPii` stays off, and this module must not undo it.
 *
 * Every function here swallows its own failure: a tag is diagnostic garnish and
 * must never turn a request into an error.
 */

import * as Sentry from "@sentry/nextjs";

/** Which part of Jana served the request. Derived from the path, see {@link surfaceOf}. */
export type Surface =
	| "storefront"
	| "panel"
	| "admin"
	| "auth"
	| "mobile-api"
	| "panel-api"
	| "rest-api"
	| "cron"
	| "webhook"
	| "payment-return"
	| "api";

const LOCALE_PREFIX = /^\/(?:ar|en)(?=\/|$)/;

/**
 * Classify a request path. Pure, so the server event processor and the browser
 * `beforeSend` agree on one answer for the same URL.
 */
export function surfaceOf(pathname: string): Surface {
	if (under(pathname, "/api")) {
		if (under(pathname, "/api/mobile")) return "mobile-api";
		if (under(pathname, "/api/manage")) return "panel-api";
		if (under(pathname, "/api/v1")) return "rest-api";
		if (under(pathname, "/api/cron")) return "cron";
		if (under(pathname, "/api/webhooks")) return "webhook";
		if (under(pathname, "/api/payments")) return "payment-return";
		return "api";
	}
	const rest = pathname.replace(LOCALE_PREFIX, "") || "/";
	if (under(rest, "/manage"))
		return /^\/manage\/(?:login|forgot|reset|change-password)(?:\/|$)/.test(rest)
			? "auth"
			: "panel";
	if (under(rest, "/admin")) return "admin";
	if (/^\/(?:login|forgot|reset|markets|invite)(?:\/|$)/.test(rest)) return "auth";
	return "storefront";
}

/** `path` is `prefix` or below it, by whole segment: `/api/mobile-legacy` is not under `/api/mobile`. */
function under(path: string, prefix: string): boolean {
	return path === prefix || path.startsWith(`${prefix}/`);
}

/** The path of an event's request, from whichever field the SDK filled. */
function eventPath(event: Sentry.Event): string | null {
	const url = event.request?.url;
	if (url) {
		try {
			return new URL(url, "http://x").pathname;
		} catch {}
	}
	// A transaction name is `GET /[locale]/checkout`: the route, with the method.
	const name = event.transaction;
	if (name) {
		const path = name.replace(/^[A-Z]+ /, "");
		if (path.startsWith("/")) return path.replace(/^\/\[locale\]/, "");
	}
	return null;
}

/**
 * Tag the event with its surface unless a caller already set one. Registered as an
 * event processor by every runtime's init.
 */
export function addSurfaceTag(event: Sentry.Event): Sentry.Event {
	if (event.tags?.surface) return event;
	const path = eventPath(event);
	if (path) event.tags = { ...event.tags, surface: surfaceOf(path) };
	return event;
}

function tag(values: Record<string, string>): void {
	Sentry.setTags(values);
	Sentry.getIsolationScope().setAttributes(values);
}

/** The market this request is for. `schema` is the tenant schema, which is what a DB error names. */
export function tagMarket(market: { slug: string; schemaName: string }): void {
	try {
		tag({ market: market.slug, "tenant.schema": market.schemaName });
	} catch {}
}

/** A signed-in staff or platform account. `role` is the membership's or the platform role. */
export function tagStaff(actor: { sub: string; role?: string | null }): void {
	try {
		Sentry.setUser({ id: `user:${actor.sub}` });
		if (actor.role) tag({ role: actor.role });
	} catch {}
}

/** A signed-in shopper. Customer ids are per tenant, so the market tag is what scopes them. */
export function tagCustomer(customer: { sub: string }): void {
	try {
		Sentry.setUser({ id: `customer:${customer.sub}` });
		tag({ role: "customer" });
	} catch {}
}

/**
 * The environment every runtime reports under. One variable for all three runtimes:
 * `NEXT_PUBLIC_` is inlined into the browser bundle at build and is an ordinary
 * environment variable on the server.
 */
export function sentryEnvironment(): string {
	return process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT?.trim() || process.env.NODE_ENV || "unknown";
}
